import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createChatSession,
  getCurrentChatSession,
  getSessionMessages,
  postRagChat,
  postRagUpload,
} from '../api/ragClient';
import { useOrg } from '../context/OrgContext';

const ACCEPT = '.pdf,.docx,.txt';
const WELCOME =
  'Ask questions about your uploaded documents. Use the upload button to add PDF, DOCX, or TXT files.';

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatError(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  return err.message || 'Something went wrong';
}

function mapStoredMessage(m) {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    sources: m.sources || [],
  };
}

export default function RagChatWidget() {
  const { branding } = useOrg();
  const primaryColor = branding.primary_color || '#B71530';

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [docIdsByFile, setDocIdsByFile] = useState({});

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const sessionLoadedRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [open, messages, scrollToBottom]);

  const initSession = useCallback(async () => {
    setLoadingSession(true);
    try {
      const data = await getCurrentChatSession();
      setSessionId(data.session_id);
      setMessages((data.messages || []).map(mapStoredMessage));
    } catch (err) {
      console.error('Failed to load chat session', err);
    } finally {
      setLoadingSession(false);
      sessionLoadedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (open && !sessionLoadedRef.current) {
      initSession();
    }
  }, [open, initSession]);

  const handleNewChat = async () => {
    try {
      const data = await createChatSession();
      setSessionId(data.session_id);
      setMessages([]);
      setDocIdsByFile({});
      sessionLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to create chat session', err);
    }
  };

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, { id: msg.id || newId(), ...msg }]);
  }, []);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || busy) return;

    setInput('');
    setBusy(true);

    try {
      const data = await postRagChat(question, sessionId);
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { id: data.user_message_id, role: 'user', content: question },
        {
          id: data.assistant_message_id,
          role: 'assistant',
          content: data.answer,
          sources: data.sources || [],
        },
      ]);
    } catch (err) {
      appendMessage({ role: 'error', content: formatError(err) });
    } finally {
      setBusy(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;

    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
    if (!['.pdf', '.docx', '.txt'].includes(ext)) {
      appendMessage({
        role: 'error',
        content: 'Unsupported file type. Please upload PDF, DOCX, or TXT.',
      });
      return;
    }

    const sourceName = file.name;
    const existingId = docIdsByFile[sourceName];
    const documentId = existingId || newId();
    const replace = Boolean(existingId);

    setUploading(true);
    const pendingId = newId();
    appendMessage({
      id: pendingId,
      role: 'system',
      content: replace ? `Re-indexing "${sourceName}"…` : `Uploading "${sourceName}"…`,
    });

    try {
      const data = await postRagUpload(file, {
        documentId,
        sourceName,
        replace,
        sessionId,
      });
      setDocIdsByFile((prev) => ({ ...prev, [sourceName]: data.document_id }));
      let sid = sessionId;
      if (!sid) {
        const session = await getCurrentChatSession();
        sid = session.session_id;
        setSessionId(sid);
      }
      const stored = await getSessionMessages(sid);
      setMessages(stored.map(mapStoredMessage));
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== pendingId));
      appendMessage({ role: 'error', content: formatError(err) });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rag-chat-root" aria-live="polite">
      {open && (
        <div className="rag-chat-panel" role="dialog" aria-label="Document assistant chat">
          <div className="rag-chat-header" style={{ backgroundColor: primaryColor }}>
            <div>
              <p className="rag-chat-header-title">Document Assistant</p>
              <p className="rag-chat-header-sub">Answers from your uploaded files only</p>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                className="rag-chat-close"
                onClick={handleNewChat}
                title="New chat"
                aria-label="New chat"
                style={{ fontSize: 14 }}
              >
                <i className="bi bi-plus-lg" />
              </button>
              <button
                type="button"
                className="rag-chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>

          <div className="rag-chat-messages">
            {loadingSession && (
              <div className="rag-chat-welcome">Loading conversation…</div>
            )}
            {!loadingSession && messages.length === 0 && (
              <div className="rag-chat-welcome">
                <i className="bi bi-chat-dots" style={{ fontSize: 28, opacity: 0.4, display: 'block', marginBottom: 8 }} />
                {WELCOME}
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`rag-chat-bubble ${m.role}`}>
                {m.content}
                {m.role === 'assistant' && m.sources?.length > 0 && (
                  <div className="rag-chat-sources">
                    <strong>Sources</strong>
                    {m.sources.join(', ')}
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="rag-chat-bubble assistant">
                <div className="rag-chat-typing" aria-label="Loading">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="rag-chat-footer">
            <input
              ref={fileInputRef}
              type="file"
              className="rag-chat-hidden-input"
              accept={ACCEPT}
              onChange={handleFileChange}
            />
            <div className="rag-chat-input-row">
              <textarea
                ref={textareaRef}
                className="rag-chat-input"
                rows={1}
                placeholder="Ask about your documents…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={busy || loadingSession}
                aria-label="Message"
              />
              <div className="rag-chat-actions">
                <button
                  type="button"
                  className="rag-chat-btn-icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || busy || loadingSession}
                  title="Upload document"
                  aria-label="Upload document"
                >
                  <i className="bi bi-paperclip" />
                </button>
                <button
                  type="button"
                  className="rag-chat-btn-send"
                  style={{ backgroundColor: primaryColor }}
                  onClick={handleSend}
                  disabled={busy || loadingSession || !input.trim()}
                  title="Send message"
                  aria-label="Send message"
                >
                  <i className="bi bi-send-fill" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`rag-chat-launcher${open ? ' is-open' : ''}`}
        style={{ backgroundColor: primaryColor }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Close document assistant' : 'Open document assistant'}
      >
        <i className={`bi bi-${open ? 'chevron-down' : 'chat-dots-fill'}`} />
      </button>
    </div>
  );
}
